import API_URL from '../config/api';
import { parseVolume, imageToTypedArray } from './niftiVolumeParser';

const getAuthHeaders = () => {
  const token = localStorage.getItem('armita_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Image cache service for CT slice data - Optimized for full volume loading
class ImageCacheService {
  constructor() {
    this.API_URL = API_URL;
    this.cache = new Map();
    this.volumeInfo = null;
    this.pendingRequests = new Map(); // Track pending requests to avoid duplicates
    this.requestQueue = [];
    this.activeRequests = 0;
    this.maxConcurrentRequests = 3; // Limit concurrent requests

    // Full volume data storage
    this.fullVolumeData = {
      ct: null,
      seg: null,
      ctHeader: null,
      segHeader: null,
      ctImage: null,
      segImage: null
    };
    this.isFullVolumeLoading = false;
    this.fullVolumeLoadPromise = null;
    // Bumped by clear(); an in-flight load from before the bump must discard
    // its results instead of repopulating the cache with the old study's CT.
    this.loadGeneration = 0;
    // Default windowing settings (matching backend defaults)
    this.windowWidth = 2000;
    this.windowCenter = 500;
  }

  // Check if volume data exists (for compatibility)
  isVolumeLoaded(viewType) {
    return !!this.fullVolumeData.ctImage;
  }

  // Load volume info and start full download
  async loadVolume(viewType) {
    try {
      // Start full volume download in background immediately
      this.loadFullVolume();

      const response = await fetch(`${this.API_URL}/nifti/info`, { headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to load volume info');
      }
      this.volumeInfo = await response.json();
      const slices = this.volumeInfo?.ct?.slices?.[viewType] || 0;
      return { slices: [], totalSlices: slices };
    } catch (err) {
      console.error('Error loading volume info:', err);
      return { slices: [], totalSlices: 0 };
    }
  }

  // Download and parse full volume
  async loadFullVolume() {
    if (this.fullVolumeData.ctImage) return; // Already loaded
    if (this.isFullVolumeLoading) return this.fullVolumeLoadPromise;

    console.log("Starting full CT volume download...");
    this.isFullVolumeLoading = true;
    const generation = this.loadGeneration;

    this.fullVolumeLoadPromise = (async () => {
      try {
        // Start downloads in parallel
        const [ctBuffer, segBuffer] = await Promise.all([
          this.fetchArrayBuffer('/nifti/download/ct'),
          this.fetchArrayBuffer('/nifti/download/seg').catch(e => null) // Seg might not exist
        ]);

        // Decompress + parse off the main thread (falls back to synchronous
        // parsing if the worker is unavailable). This is where the ~60 MB gzip
        // inflate used to freeze the UI.
        console.log("CT Volume downloaded, parsing...");
        const parsed = await this._parseVolumesOffThread(ctBuffer, segBuffer);

        if (generation !== this.loadGeneration) {
          console.log("CT volume load superseded by clear(); discarding result");
          return;
        }

        if (parsed.ct) {
          this.fullVolumeData.ctHeader = parsed.ct.header;
          this.fullVolumeData.ctImage = imageToTypedArray(
            parsed.ct.image, parsed.ct.header.datatypeCode
          );
          console.log("CT Volume parsed successfully.", this.fullVolumeData.ctImage.length, "elements");
        }

        if (parsed.seg) {
          this.fullVolumeData.segHeader = parsed.seg.header;
          this.fullVolumeData.segImage = new Uint8Array(parsed.seg.image); // Seg is usually uint8
        }

        console.log("Full volume loaded into memory!");
      } catch (e) {
        console.error("Full volume load failed", e);
      } finally {
        if (generation === this.loadGeneration) {
          this.isFullVolumeLoading = false;
        }
      }
    })();
    return this.fullVolumeLoadPromise;
  }

  async fetchArrayBuffer(url) {
    const res = await fetch(`${this.API_URL}${url}`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error(res.statusText + " " + url);
    return res.arrayBuffer();
  }

  // Parse CT + seg buffers in a Web Worker so the gzip inflate doesn't block the
  // UI thread. Degrades to synchronous main-thread parsing if a worker can't be
  // created (unsupported environment, bundling failure) or dies mid-parse, so
  // behaviour never regresses below the previous implementation.
  _parseVolumesOffThread(ctBuffer, segBuffer) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (!settled) {
          settled = true;
          resolve(value);
        }
      };

      let worker;
      try {
        worker = new Worker(new URL('../workers/ctVolume.worker.js', import.meta.url));
      } catch (e) {
        console.warn('CT volume worker unavailable, parsing on main thread:', e);
        finish(this._parseVolumesSync(ctBuffer, segBuffer));
        return;
      }

      const fallback = (reason) => {
        if (settled) return;
        console.warn('CT volume worker failed, parsing on main thread:', reason);
        try { worker.terminate(); } catch (_) { /* ignore */ }
        finish(this._parseVolumesSync(ctBuffer, segBuffer));
      };

      worker.onmessage = (event) => {
        try { worker.terminate(); } catch (_) { /* ignore */ }
        if (event.data && event.data.ok) {
          finish(event.data.result);
        } else {
          fallback(event.data && event.data.error);
        }
      };
      worker.onerror = (event) => fallback((event && event.message) || event);

      // Copy (don't transfer) the input buffers so this thread keeps them intact
      // for the fallback path; the copy is cheap next to the inflate. The large
      // decompressed image buffers are transferred back by the worker.
      worker.postMessage({ ct: ctBuffer, seg: segBuffer || null });
    });
  }

  _parseVolumesSync(ctBuffer, segBuffer) {
    return {
      ct: ctBuffer ? parseVolume(ctBuffer) : null,
      seg: segBuffer ? parseVolume(segBuffer) : null,
    };
  }

  // Process the request queue
  async processQueue() {
    while (this.requestQueue.length > 0 && this.activeRequests < this.maxConcurrentRequests) {
      const request = this.requestQueue.shift();
      this.activeRequests++;

      request().finally(() => {
        this.activeRequests--;
        this.processQueue(); // Process next item in queue
      });
    }
  }

  // Fetch a single slice with request management
  async fetchSlice(viewType, sliceIndex) {
    const cacheKey = `${viewType}_${sliceIndex}`;

    // 1. Check cache first
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // 2. Ensure full volume is loaded or loading
    if (!this.fullVolumeData.ctImage) {
      if (!this.isFullVolumeLoading) {
        console.log("fetchSlice: Triggering full volume load due to missing data");
        this.loadFullVolume();
      }

      try {
        // Wait for the volume to load
        await this.fullVolumeLoadPromise;
      } catch (e) {
        console.error("fetchSlice: Full volume load failed, falling back to API", e);
        // If full load failed, we will fall through to API fallback code below
      }
    }

    // 3. Try to serve from full volume memory (Retry after potential load)
    if (this.fullVolumeData.ctImage) {
      try {
        const sliceData = this.extractSliceFromVolume(viewType, sliceIndex);
        if (sliceData) {
          this.cache.set(cacheKey, sliceData);
          return sliceData;
        }
      } catch (e) {
        console.error("Error extracting slice from volume:", e);
      }
    }

    // 4. Fallback to API (Only if full volume load failed or extraction failed)

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create the fetch promise (fallback to API)
    const fetchPromise = new Promise((resolve, reject) => {
      const doFetch = async () => {
        try {
          const response = await fetch(
            `${this.API_URL}/nifti/slice-data?view_type=${viewType}&slice_index=${sliceIndex}`,
            { headers: getAuthHeaders() }
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch slice: ${response.statusText}`);
          }

          const data = await response.json();

          // Cache the result
          this.cache.set(cacheKey, data);

          // Manage cache size
          if (this.cache.size > 2000) {
            const firstKey = this.cache.keys().next().value;
            this.cache.delete(firstKey);
          }

          this.pendingRequests.delete(cacheKey);
          resolve(data);
        } catch (err) {
          console.error(`Error fetching slice ${sliceIndex}:`, err);
          this.pendingRequests.delete(cacheKey);
          resolve(null);
        }
      };

      this.requestQueue.push(doFetch);
    });

    this.pendingRequests.set(cacheKey, fetchPromise);
    this.processQueue();
    return fetchPromise;
  }

  extractSliceFromVolume(viewType, sliceIndex) {
    if (!this.fullVolumeData.ctImage || !this.fullVolumeData.ctHeader) return null;

    const header = this.fullVolumeData.ctHeader;
    const dims = header.dims; // [ndim, x, y, z, t, ...]
    const width = dims[1];
    const height = dims[2];
    const depth = dims[3];

    const ctData = this.fullVolumeData.ctImage;
    const segData = this.fullVolumeData.segImage;

    // Rescaling parameters
    const slope = header.scl_slope || 1;
    const inter = header.scl_inter || 0;

    // Windowing parameters
    const windowMin = this.windowCenter - this.windowWidth / 2;
    const windowMax = this.windowCenter + this.windowWidth / 2;
    const windowRange = windowMax - windowMin;

    let resultWidth, resultHeight;
    let getIndex; // Function to get volume index from (localX, localY)

    const view = viewType.toLowerCase();

    if (view === 'axial') {
      // Z-slice. Size: X * Y
      resultWidth = width;
      resultHeight = height;
      // Index = x + y*width + z*width*height
      // Fix: Rotate 180 degrees by flipping both X and Y
      // Original was: c + r * width + ...
      // New is: (width - 1 - c) + (height - 1 - r) * width + ...
      getIndex = (c, r) => (width - 1 - c) + (height - 1 - r) * width + sliceIndex * width * height;
    } else if (view === 'coronal') {
      // Y-slice. Size: X * Z
      resultWidth = width;
      resultHeight = depth;
      // Index = x + y*width + z*width*height
      // Rotate 180 degrees:
      // x -> (width - 1 - c)
      // z -> (depth - 1 - r)
      getIndex = (c, r) => (width - 1 - c) + sliceIndex * width + (depth - 1 - r) * width * height;
    } else if (view === 'sagittal') {
      // X-slice. Size: Y * Z
      resultWidth = height;
      resultHeight = depth;
      // Index = x + y*width + z*width*height
      // Rotate 180 degrees:
      // y -> (height - 1 - c)
      // z -> (depth - 1 - r)
      getIndex = (c, r) => sliceIndex + (height - 1 - c) * width + (depth - 1 - r) * width * height;
    } else {
      return null;
    }

    // Output arrays
    const size = resultWidth * resultHeight;
    const outCt = new Uint8Array(size); // 0-255 windowed
    const outSeg = segData ? new Uint8Array(size) : null;

    let ptr = 0;
    for (let r = 0; r < resultHeight; r++) {
      for (let c = 0; c < resultWidth; c++) {
        let volIdx = getIndex(c, r);

        // Boundary check
        if (volIdx >= ctData.length) volIdx = ctData.length - 1;

        // Read value
        const rawVal = ctData[volIdx];
        // Apply rescaling
        const valHU = rawVal * slope + inter;
        // Apply windowing
        let valNorm = (valHU - windowMin) / windowRange * 255;
        valNorm = Math.max(0, Math.min(255, valNorm));

        outCt[ptr] = valNorm;

        if (segData && outSeg) {
          outSeg[ptr] = segData[volIdx] || 0;
        }
        ptr++;
      }
    }

    // Check if we need to transpose to match backend orientation
    // Backend:
    // Axial (X, Y) -> Transpose to (Y, X)
    // Coronal (X, Z) -> (X, Z) (No transpose?)
    // Sagittal (Y, Z) -> (Y, Z) (No transpose?)

    // My loop above produces (Width, Height) = (c, r).
    // If Axial resultWidth=Width=X, resultHeight=Height=Y.
    // So I produced (X, Y).
    // If backend transposes Axial to (Y, X), I should too.
    // Or just swap width/height in result and mapping?

    // Let's assume standard orientation for now. If it's rotated, I'll fix it.
    // Actually, standard medical viewers often show Axial as Anterior-Posterior (Y) on vertical axis, Left-Right (X) on horizontal.
    // So (X, Y) is correct (width=X, height=Y).
    // Why did backend transpose? Maybe nibabel vs numpy axis order.
    // Numpy: (Z, Y, X)? 
    // Nibabel `get_fdata()` returns (X, Y, Z).

    return {
      ct_data: outCt,
      seg_data: outSeg ? outSeg : [],
      width: resultWidth,
      height: resultHeight,
      seg_width: resultWidth,
      seg_height: resultHeight,
      slice_index: sliceIndex,
      view_type: viewType,
      vertebrae: {},
      prominent_vertebra: null
    };
  }

  // Preload slices - updated to act as cache warmer or no-op if full volume loading
  async preloadSlices(viewType, currentSlice, totalSlices, radius = 10) {
    if (this.fullVolumeData.ctImage) {
      return; // Instant access, no need to preload
    }

    // Fallback
    const start = Math.max(0, currentSlice - radius);
    const end = Math.min(totalSlices - 1, currentSlice + radius);

    for (let i = start; i <= end; i++) {
      if (i !== currentSlice) {
        this.fetchSlice(viewType, i).catch(e => { });
      }
    }
  }

  clear() {
    this.loadGeneration++;
    this.cache.clear();
    this.volumeInfo = null;
    this.fullVolumeData = { ct: null, seg: null, ctHeader: null, segHeader: null, ctImage: null, segImage: null };
    this.isFullVolumeLoading = false;
    this.fullVolumeLoadPromise = null;
    this.pendingRequests.clear();
    this.requestQueue.length = 0;
  }

  clearViewType(viewType) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${viewType}_`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

const imageCache = new ImageCacheService();
export default imageCache;